import type { MaybeRef, MaybeRefOrGetter, Ref } from "vue";
import { ref, toRef, toValue, unref, watch, computed, isRef } from "vue";
import type { UseQueryReturnType } from "@tanstack/vue-query";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { OneOrManyOrNull, OrNull } from "@/types/general";

export type TComposableParams = {
  enabled?: MaybeRefOrGetter<boolean>;
  lazy?: boolean;
  usePublic?: boolean;
};

/**
 * The control keys of `TComposableParams`. Single source of truth for the `*ForSelect`
 * helpers, which receive control and filter fields flattened into one object (the shape
 * `TListComposable` requires) and must separate them again.
 *
 * Declared `satisfies` the key set so adding a key to `TComposableParams` without listing
 * it here is a compile error — otherwise the new key would be silently reclassified as an
 * API filter at every select helper.
 */
export const COMPOSABLE_PARAM_KEYS = [
  "enabled",
  "lazy",
  "usePublic",
] as const satisfies readonly (keyof TComposableParams)[];

/**
 * Splits a flattened select-helper params object into API filters and control params.
 *
 * Name-based by necessity: a filter field genuinely called `enabled` / `lazy` / `usePublic`
 * would be swallowed. No current API filter interface uses those names.
 */
export function splitComposableParams<TFilters extends object>(
  params: (Partial<TFilters> & TComposableParams) | undefined
): { filters: Partial<TFilters>; control: TComposableParams } {
  const rest = { ...params } as Record<string, unknown>;
  const control: Record<string, unknown> = {};
  for (const key of COMPOSABLE_PARAM_KEYS) {
    if (key in rest) {
      control[key] = rest[key];
      delete rest[key];
    }
  }
  return { filters: rest as Partial<TFilters>, control: control as TComposableParams };
}

/**
 * Resolves the effective `enabled` for a select helper: `lazy` forces the query closed
 * until the select opens its gate, otherwise the caller's `enabled` (default true).
 *
 * Call this *inside* a getter passed as `enabled` — resolving it eagerly with `toValue` at
 * setup freezes a reactive source at its first value.
 */
export function resolveSelectEnabled(
  params: (Record<string, unknown> & TComposableParams) | undefined
): boolean {
  if (params?.lazy) return false;
  return toValue(params?.enabled ?? true);
}

export type TPaginatedQueryComposableParams = TComposableParams;

export type TItemQueryComposableParams<T = string | number> = TComposableParams & {
  id?: MaybeRefOrGetter<OrNull<T | undefined>>;
};

export type TItemsQueryComposableParams = TComposableParams & {
  ids?: MaybeRefOrGetter<OneOrManyOrNull<string | number>>;
};

type TListComposable<TItem, TFilters extends DefaultPaginatedFilters> = (
  params: TComposableParams & Partial<TFilters>
) => TSelectFieldDynamicProps<TItem, TFilters>;

type TItemQueryHook<TItem, TId> = (
  params: TItemQueryComposableParams<TId>
) => UseQueryReturnType<OrNull<TItem>, Error>;

// type TItemsQueryHook<TItem> = (
//   params: TItemsQueryComposableParams
// ) => UseQueryReturnType<OneOrManyOrNull<TItem>, Error>;

// Helper to extract the list data type from the hook return type
export const useGeneralFilterOne = <
  TItem,
  TFilters extends DefaultPaginatedFilters,
  TId = string | number,
>(
  listComposable: TListComposable<TItem, TFilters>,
  itemQueryHook: TItemQueryHook<TItem, TId>,
  params: MaybeRef<
    {
      enabledList?: MaybeRef<boolean>;
      enabledItem?: MaybeRef<boolean>;
    } & Partial<TFilters> &
      Omit<TComposableParams, "enabled">
  >
): TSelectFieldDynamicProps<TItem, TFilters, TId> => {
  const paramsRef = toRef(params);

  // Use the existing composable to get list query and filters
  // We use the initial value of params to initialize the composable
  const { enabledItem, enabledList, ...rest } = unref(paramsRef);

  const { paginatedQuery, queryAll, queryFilters, queryEnabled, filterField } = (
    listComposable as any
  )({
    ...(rest as Partial<TFilters> & Omit<TComposableParams, "enabled">),
    enabled: enabledList,
  });

  // Watch for changes in params and update the filters.
  //
  // `queryEnabled` is the lazy gate that the select itself opens on demand (single-item
  // check / interaction / mobile dialog). We must only re-push `enabledList` onto it when
  // `enabledList` *itself* changes — not on every filter change. Otherwise a dependent
  // select (computed filters, static `enabledList: false`) gets its gate slammed shut on
  // each filter update; pre-`skipToken` the select's forced `refetch()` masked that, but
  // with `enabled` now authoritative the refetch is a no-op and the list silently stops
  // loading.
  let prevEnabledList = toValue(unref(paramsRef).enabledList);
  watch(
    paramsRef,
    (newParams) => {
      const {
        enabledList: _el,
        enabledItem: _ei,
        lazy: _l,
        usePublic: _up,
        ...newRest
      } = unref(newParams);
      if (queryFilters && typeof queryFilters === "object" && "value" in queryFilters) {
        Object.assign(queryFilters.value, newRest);
      }
      const nextEnabledList = toValue(_el);
      if (queryEnabled && isRef(queryEnabled) && nextEnabledList !== prevEnabledList) {
        (queryEnabled as Ref<boolean>).value = nextEnabledList ?? true;
      }
      prevEnabledList = nextEnabledList;
    },
    { deep: true }
  );

  // Item Query (for single item fetching)
  const itemQueryRef = ref<OrNull<TId>>(null) as Ref<OrNull<TId>>;
  const itemQuery = itemQueryHook({
    id: itemQueryRef,
    enabled: computed(() => unref(unref(paramsRef).enabledItem)),
    ...(rest as Partial<TFilters> & Omit<TComposableParams, "enabled">),
  });

  // Force cast the return object to match what SelectFieldValidator expects
  // We use 'any' here because TListQueryHook returns a union of Infinite/Normal query,
  // and SelectFieldValidator handles both, but TS struggles with the union discrimination in the return object.
  return {
    paginatedQuery,
    queryAll,
    queryFilters,
    itemQuery,
    itemQueryRef,
    queryEnabled,
    filterField,
  } as any;
};
